<script setup>
import { onMounted, ref, watch } from "vue";
import dayjs from "dayjs";
import { useListStoreNew } from "@/stores/ListStoreNew";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import ListSearch from "@/components/ListSearch.vue";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import { usePagination } from "@/composables/usePagination";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

const list = useListStoreNew();
const route = useRoute();
const router = useRouter();
const filter = ref("all");
const isReady = ref(false);
const sortBy = ref("updated_at");
const sortDirection = ref("DESC");
const sortOptions = [
  { value: "updated_at", label: "Updated" },
  { value: "created_at", label: "Created" },
  { value: "id", label: "ID" },
];
const { setUrl, changePage } = usePagination(list, filter, () => ({
  sort: sortBy.value,
  direction: sortDirection.value,
}));
const { setBreadcrumb } = useBreadcrumbs();

const applyFilter = () => {
  list.clearFilters();
  if (filter.value && filter.value !== "all") {
    list.addFilter("status", filter.value);
  }
};

const applySort = () => {
  if (!list.listService) return;
  list.listService.column = sortBy.value;
  list.listService.direction = sortDirection.value;
};

const load = () => {
  if (!list.listService) return;
  applyFilter();
  applySort();
  setUrl();
  list.resetPage();
};

const setFilter = (val) => {
  filter.value = val;
  load();
};

const setSort = (column) => {
  if (sortBy.value === column) {
    sortDirection.value = sortDirection.value === "ASC" ? "DESC" : "ASC";
  } else {
    sortBy.value = column;
    sortDirection.value = "DESC";
  }
};

const sortIndicator = (column) => {
  if (sortBy.value !== column) {
    return "";
  }
  return sortDirection.value === "ASC" ? "▲" : "▼";
};

const sortClass = (column) =>
  sortBy.value === column
    ? "text-slate-900 dark:text-slate-100"
    : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300";

const statusClass = (status) => {
  if (status === "sent") return "bg-emerald-500";
  if (status === "accepted") return "bg-sky-500";
  if (status === "declined") return "bg-rose-500";
  return "bg-slate-500";
};

const statusLabel = (status) => {
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : "Draft";
};

const quoteRoute = (quote) => {
  const path = `/quotes/${quote.id}`;
  const ticketId = Number(quote?.ticket_id);
  if (!Number.isNaN(ticketId) && ticketId > 0) {
    return { path, query: { ticket_id: ticketId } };
  }

  return { path };
};

const quoteName = (quote) =>
  quote?.reference?.name ||
  quote?.display_name ||
  quote?.versions?.[0]?.event_risk?.event_name ||
  quote?.versions?.[0]?.pts_risk?.patient_name ||
  quote?.description ||
  "-";

const quoteCode = (quote) =>
  quote?.reference?.txt || `Quote #${quote?.id ?? ""}`;

onMounted(() => {
  list.configure({ remote: "/api/quote", local: "/quotes" });
  filter.value = route.query?.status ?? "all";
  sortBy.value = route.query?.sort ?? "updated_at";
  sortDirection.value = route.query?.direction ?? "DESC";
  applyFilter();
  applySort();
  list.fetchIndex();
  isReady.value = true;
  setBreadcrumb("Quotes");
});

onBeforeRouteUpdate((to, from, next) => {
  if (to.query.page === undefined) {
    load();
  }
  next();
});

watch([sortBy, sortDirection], () => {
  if (!list.listService || !isReady.value) {
    return;
  }
  load();
});
</script>

<template>
  <div>
    <div class="box">
      <div class="flex flex-wrap items-center gap-3 lg:gap-4">
        <ListSearch
          class="flex-1 mr-4"
          url="/api/quote/search?query="
          :output="(val) => quoteName(val)"
          @selected="(val) => router.push(quoteRoute(val))"
        />
        <div class="flex items-center gap-2 text-xs text-slate-500">
          <span class="hidden md:inline">Sort</span>
          <select v-model="sortBy" class="input text-xs rounded-md">
            <option
              v-for="option in sortOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <button
            class="btn h-9 px-3 rounded-md"
            @click.prevent="setSort(sortBy)"
          >
            {{ sortDirection === "ASC" ? "Asc" : "Desc" }}
          </button>
        </div>
        <RouterLink class="btn h-9 px-4 rounded-md" to="/quotes/create"
          >Create Quote</RouterLink
        >
      </div>
      <div class="grid grid-cols-1 md:grid-cols-5 mt-4 gap-2">
        <button
          class="btn-plain bg-sky-500"
          @click.prevent="() => setFilter('all')"
          :class="{ 'ring-2 ring-white/80': filter === 'all' }"
        >
          All
        </button>
        <button
          class="btn-plain bg-teal-500"
          @click.prevent="() => setFilter('draft')"
          :class="{ 'ring-2 ring-white/80': filter === 'draft' }"
        >
          Draft
        </button>
        <button
          class="btn-plain bg-emerald-500"
          @click.prevent="() => setFilter('sent')"
          :class="{ 'ring-2 ring-white/80': filter === 'sent' }"
        >
          Sent
        </button>
        <button
          class="btn-plain bg-sky-500"
          @click.prevent="() => setFilter('accepted')"
          :class="{ 'ring-2 ring-white/80': filter === 'accepted' }"
        >
          Accepted
        </button>
        <button
          class="btn-plain bg-rose-500"
          @click.prevent="() => setFilter('declined')"
          :class="{ 'ring-2 ring-white/80': filter === 'declined' }"
        >
          Declined
        </button>
      </div>
    </div>

    <table class="box list" v-if="!list.isLoading">
      <thead class="hidden xl:table-header-group">
        <tr>
          <th class="p-3 truncate max-w-36 lg:max-w-96">
            <button
              class="inline-flex items-center gap-1"
              :class="sortClass('updated_at')"
              @click.prevent="setSort('updated_at')"
            >
              Event / Job
              <span class="text-[10px]">{{ sortIndicator("updated_at") }}</span>
            </button>
          </th>
          <th class="p-3 max-w-32 truncate hidden lg:table-cell">Customer</th>
          <th class="p-3 truncate max-w-36 lg:max-w-12 hidden lg:table-cell">
            Type
          </th>
          <th class="p-3 truncate max-w-36 lg:max-w-12 hidden lg:table-cell">
            Status
          </th>
          <th class="p-3 truncate max-w-36 lg:max-w-12 hidden xl:table-cell">
            <button
              class="inline-flex items-center gap-1"
              :class="sortClass('updated_at')"
              @click.prevent="setSort('updated_at')"
            >
              Updated
              <span class="text-[10px]">{{ sortIndicator("updated_at") }}</span>
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="list.getIndex.length === 0">
          <td colspan="5" class="p-4 text-center text-slate-500">
            No quotes found.
          </td>
        </tr>
        <tr
          v-for="quote in list.getIndex"
          :key="quote.id"
          class="cursor-pointer"
          @click="$router.push(quoteRoute(quote))"
        >
          <td class="p-3 max-w-36 lg:max-w-96">
            <div class="flex flex-col gap-1">
              <span
                class="font-semibold text-slate-900 dark:text-slate-100 truncate"
              >
                {{ quoteName(quote) }}
              </span>
              <span class="text-xs text-slate-400">{{ quoteCode(quote) }}</span>
              <span class="text-xs text-slate-500 lg:hidden">
                {{ quote.customer?.company ?? "Unknown customer" }} •
                {{ quote.reference_type?.name ?? "Unknown type" }} •
                {{ statusLabel(quote.status) }}
              </span>
            </div>
          </td>
          <td class="p-3 max-w-32 truncate hidden lg:table-cell">
            {{ quote.customer?.company ?? "-" }}
          </td>
          <td class="p-3 truncate max-w-36 lg:max-w-12 hidden lg:table-cell">
            {{ quote.reference_type?.name ?? "-" }}
          </td>
          <td
            class="p-3 overflow-clip max-w-36 lg:max-w-12 hidden lg:table-cell"
          >
            <label
              class="label-clickable w-20 text-xs"
              :class="statusClass(quote.status)"
            >
              {{ statusLabel(quote.status) }}
            </label>
          </td>
          <td class="p-3 truncate max-w-36 lg:max-w-12 hidden xl:table-cell">
            <span :title="dayjs(quote?.updated_at).format('LL')">
              {{ quote?.updated_at ? dayjs(quote.updated_at).fromNow() : "-" }}
            </span>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="5" class="p-4" v-if="list.getIndex.length < 1">
            No data to display
          </td>
        </tr>
        <tr>
          <th colspan="5" class="p-4">
            <PrevNextButtons
              :showPrev="list.showPrev"
              :showNext="list.showNext"
              @prev="changePage(-1)"
              @next="changePage(1)"
            />
          </th>
        </tr>
      </tfoot>
    </table>
    <table class="box list" v-else>
      <tbody>
        <tr
          v-for="n in 12"
          :key="n"
          class="border-b border-gray-300 animate-pulse"
        >
          <td class="p-3 truncate max-w-36 lg:max-w-96">
            <div class="h-6 bg-gray-300 rounded"></div>
          </td>
          <td class="p-3 max-w-32 truncate hidden lg:table-cell">
            <div class="h-6 bg-gray-300 rounded"></div>
          </td>
          <td class="p-3 truncate max-w-36 lg:max-w-12 hidden lg:table-cell">
            <div class="h-6 bg-gray-300 rounded"></div>
          </td>
          <td class="p-3 truncate max-w-36 lg:max-w-12 hidden lg:table-cell">
            <div class="h-6 bg-gray-300 rounded"></div>
          </td>
          <td class="p-3 truncate max-w-36 lg:max-w-12 hidden xl:table-cell">
            <div class="h-6 bg-gray-300 rounded"></div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
